import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import { it as itLocale } from 'date-fns/locale';
import { dashboardApi, aiApi, staffApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import FreshaUnmatchedBanner from '../components/FreshaUnmatchedBanner';
import {
  Store as StoreIcon, TrendingUp, TrendingDown, Sparkles, RefreshCw,
  ClipboardList, ChevronUp, ChevronDown, X, Building2, MapPin, ExternalLink, Clock,
} from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as ChartTooltip, CartesianGrid, Legend } from 'recharts';
import { usePersistedPeriod } from '../lib/usePersistedPeriod';
import { useUrlState } from '../lib/useUrlState';
import ProfitabilitySection from '../components/ProfitabilitySection';
import TopCostsChart from '../components/TopCostsChart';
import HealthBadge from '../components/HealthBadge';
import DateRangePicker from '../components/DateRangePicker';
import { SavedViewsControl } from '../components/SavedViewsControl';
import { CopyLinkButton } from '../components/CopyLinkButton';
import { SeriesTooltipContent } from '../components/SeriesTooltipContent';
import { semaphoreFromPct, semaphoreFromPp, SEMAPHORE_TEXT } from '../lib/semaphore';

const MONTHS = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const MONTH_SHORT = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];

const fmtEUR = (v) => v == null ? '—' : new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const fmtPct = (v) => v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;

export default function OverviewPage() {
  const { user } = useAuth();
  const isOps = user?.role === 'operations_manager';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // `usePersistedPeriod` resta presente per coerenza cross-pagina (Area/Store
  // continuano a usare period/year). La Panoramica ora guida da un date range
  // giornaliero (`dateFrom`/`dateTo`), e tiene period/year allineati al mese
  // del `dateFrom` come "mese rappresentativo" del range — così atterrando
  // su una Store Dashboard via drill-down si vede lo stesso mese senza
  // doverlo riselezionare.
  const { period, setPeriod, year, setYear } = usePersistedPeriod('YEAR', 2026);
  const [refreshKey, setRefreshKey] = useState(0);

  // Date range URL-persistito. Defaults: mese corrente. Formato ISO date.
  const today = useMemo(() => new Date(), []);
  const defaultFrom = useMemo(() => format(startOfMonth(today), 'yyyy-MM-dd'), [today]);
  const defaultTo = useMemo(() => format(endOfMonth(today), 'yyyy-MM-dd'), [today]);
  const [dateFrom, setDateFrom] = useUrlState('date_from', defaultFrom, {
    parse: (s) => s || defaultFrom,
    serialize: (v) => v || '',
    isDefault: (v) => v === defaultFrom,
  });
  const [dateTo, setDateTo] = useUrlState('date_to', defaultTo, {
    parse: (s) => s || defaultTo,
    serialize: (v) => v || '',
    isDefault: (v) => v === defaultTo,
  });

  const [selectedStoreIds, setSelectedStoreIds] = useUrlState('stores', [], {
    parse: (s) => s ? s.split(',').filter(Boolean) : [],
    serialize: (arr) => Array.isArray(arr) ? arr.join(',') : '',
    isDefault: (arr) => !Array.isArray(arr) || arr.length === 0,
  });
  const [selectedBrandIds, setSelectedBrandIds] = useUrlState('brands', [], {
    parse: (s) => s ? s.split(',').filter(Boolean) : [],
    serialize: (arr) => Array.isArray(arr) ? arr.join(',') : '',
    isDefault: (arr) => !Array.isArray(arr) || arr.length === 0,
  });
  const [selectedAreaIds, setSelectedAreaIds] = useUrlState('areas', [], {
    parse: (s) => s ? s.split(',').filter(Boolean) : [],
    serialize: (arr) => Array.isArray(arr) ? arr.join(',') : '',
    isDefault: (arr) => !Array.isArray(arr) || arr.length === 0,
  });
  const [storeFilterOpen, setStoreFilterOpen] = useState(false);
  const [storeFilterQuery, setStoreFilterQuery] = useState('');
  const [availableStores, setAvailableStores] = useState([]);
  const [availableBrands, setAvailableBrands] = useState([]);
  const [availableAreas, setAvailableAreas] = useState([]);

  // Pool di store visibili nel multi-select dipende dai filtri brand + area.
  // L'AND tra i due viene replicato qui per la UI (il backend già lo applica
  // sui dati restituiti, ma il filtro pop-over deve riflettere il subset).
  const visibleStores = useMemo(() => {
    let pool = availableStores;
    if (selectedBrandIds && selectedBrandIds.length > 0) {
      const wanted = new Set(selectedBrandIds);
      pool = pool.filter(s => wanted.has(s.brand_id));
    }
    if (selectedAreaIds && selectedAreaIds.length > 0) {
      const wanted = new Set(selectedAreaIds);
      pool = pool.filter(s => wanted.has(s.area));
    }
    return pool;
  }, [availableStores, selectedBrandIds, selectedAreaIds]);

  // Mese/anno "rappresentativo" del range (= mese di `dateFrom`). Backend
  // aggrega il mese di `date_from` come "vista parziale" quando il range
  // non copre un mese intero. Usato per Health Score + ExecutiveSummary
  // (che restano metriche mensili per natura).
  const repMonth = useMemo(() => {
    try { return parseISO(dateFrom).getMonth() + 1; } catch { return today.getMonth() + 1; }
  }, [dateFrom, today]);
  const repYear = useMemo(() => {
    try { return parseISO(dateFrom).getFullYear(); } catch { return today.getFullYear(); }
  }, [dateFrom, today]);

  // Tieni `period/year` allineato al mese rappresentativo del range, così
  // un drill-down a Store/Area Dashboard atterra sul mese giusto senza
  // forzare l'utente a riselezionare. Strict equality per evitare loop.
  useEffect(() => {
    if (period !== String(repMonth)) setPeriod(String(repMonth));
    if (year !== repYear) setYear(repYear);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repMonth, repYear]);

  useEffect(() => {
    setLoading(true);
    const params = { date_from: dateFrom, date_to: dateTo };
    if (selectedStoreIds.length > 0) params.store_ids = selectedStoreIds.join(',');
    if (selectedBrandIds.length > 0) params.brand_ids = selectedBrandIds.join(',');
    if (selectedAreaIds.length > 0) params.area_ids = selectedAreaIds.join(',');
    dashboardApi.overview(params)
      .then(r => {
        setData(r.data);
        // Aggiorna la pool del multi-select solo quando NON c'è un filtro store attivo,
        // così l'utente vede sempre tutto l'elenco quando apre il popover.
        if (Array.isArray(r.data?.available_stores)) {
          setAvailableStores(r.data.available_stores);
        }
        if (Array.isArray(r.data?.available_brands)) {
          setAvailableBrands(r.data.available_brands);
        }
        if (Array.isArray(r.data?.available_areas)) {
          setAvailableAreas(r.data.available_areas);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, refreshKey, selectedStoreIds, selectedBrandIds, selectedAreaIds]);

  useEffect(() => {
    const bump = () => setRefreshKey(k => k + 1);
    const onVis = () => { if (document.visibilityState === 'visible') bump(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('storeops:data-changed', bump);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('storeops:data-changed', bump);
    };
  }, []);

  const periodLabel = useMemo(() => {
    try {
      const f = parseISO(dateFrom);
      const t = parseISO(dateTo);
      const sameDay = format(f, 'yyyy-MM-dd') === format(t, 'yyyy-MM-dd');
      if (sameDay) return format(f, 'd MMM yyyy', { locale: itLocale });
      // Range esattamente coincidente con un mese intero → mostra il mese.
      const fMonthStart = format(startOfMonth(f), 'yyyy-MM-dd');
      const fMonthEnd = format(endOfMonth(f), 'yyyy-MM-dd');
      if (fMonthStart === format(f, 'yyyy-MM-dd') && fMonthEnd === format(t, 'yyyy-MM-dd')) {
        return format(f, 'MMMM yyyy', { locale: itLocale });
      }
      return `${format(f, 'd MMM', { locale: itLocale })} → ${format(t, 'd MMM yyyy', { locale: itLocale })}`;
    } catch {
      return '—';
    }
  }, [dateFrom, dateTo]);

  return (
    <div className="p-4 md:p-8 max-w-[1920px]" data-testid="overview-page">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Panoramica</h1>
          <p className="text-sm md:text-base text-slate-500 mt-1">
            Polso del business — {data?.store_count || 0} negozi attivi · {periodLabel}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap w-full sm:w-auto">
          <DateRangePicker
            from={dateFrom}
            to={dateTo}
            onChange={(f, t) => { setDateFrom(f); setDateTo(t); }}
            testIdPrefix="overview-daterange"
          />
        </div>
      </div>

      <FreshaUnmatchedBanner />

      {user?.role === 'area_manager' && !loading && data && (data.store_count || 0) === 0 && (
        <div className="bg-red-50 border border-red-200 px-4 py-3 mb-6 text-sm text-red-900" data-testid="am-not-linked-banner">
          <span className="font-semibold">Nessun negozio visibile.</span>{' '}
          Il tuo account non risulta collegato a un'anagrafica Area Manager (o l'anagrafica non ha negozi assegnati).
          Chiedi a un OPS di collegarlo da <span className="font-semibold">Impostazioni → Mappatura Negozi → Area Manager</span>.
        </div>
      )}

      {/* Filtro store + brand + area — coerente con Dashboard Area. */}
      <div className="bg-white border border-slate-200 px-4 py-3 mb-6 flex flex-wrap items-center gap-3" data-testid="overview-filters-bar">        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mr-1">Filtro</span>
        <BrandFilter
          brands={availableBrands}
          selected={selectedBrandIds}
          onChange={(next) => {
            setSelectedBrandIds(next);
            // Quando cambio set di brand, rimuovo dagli store_ids quelli che
            // non appartengono più al sottoinsieme visibile, così non resta
            // un filtro "fantasma" applicato a uno store ora invisibile.
            if (next.length > 0 && selectedStoreIds.length > 0) {
              const visibleIds = new Set(
                availableStores
                  .filter(s => next.includes(s.brand_id))
                  .map(s => s.store_id)
              );
              setSelectedStoreIds(prev => prev.filter(id => visibleIds.has(id)));
            }
          }}
        />
        <AreaFilter
          areas={availableAreas}
          selected={selectedAreaIds}
          onChange={(next) => {
            setSelectedAreaIds(next);
            // Stessa logica del brand: se filtro le aree, rimuovi gli store
            // appena diventati invisibili dal multiselect.
            if (next.length > 0 && selectedStoreIds.length > 0) {
              const visibleIds = new Set(
                availableStores
                  .filter(s => next.includes(s.area))
                  .map(s => s.store_id)
              );
              setSelectedStoreIds(prev => prev.filter(id => visibleIds.has(id)));
            }
          }}
        />
        <Popover open={storeFilterOpen} onOpenChange={setStoreFilterOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="rounded-none h-9 px-3 justify-between font-normal min-w-[14rem]"
              data-testid="overview-stores-filter-trigger"
            >
              <span className="flex items-center gap-2 text-sm truncate">
                <StoreIcon className="w-4 h-4 text-slate-500" />
                {selectedStoreIds.length === 0
                  ? `Tutti i negozi (${visibleStores.length || data?.store_count || 0})`
                  : `${selectedStoreIds.length} negozi selezionati`}
              </span>
              {selectedStoreIds.length > 0 && (
                <X
                  className="w-3.5 h-3.5 text-slate-500 hover:text-slate-900 ml-2"
                  data-testid="overview-stores-filter-clear"
                  onClick={(e) => { e.stopPropagation(); setSelectedStoreIds([]); }}
                />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0 rounded-none" align="start">
            <div className="p-2 border-b border-slate-100">
              <input
                type="text"
                value={storeFilterQuery}
                onChange={(e) => setStoreFilterQuery(e.target.value)}
                placeholder="Cerca negozio..."
                className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-none focus:outline-none focus:border-slate-900"
                data-testid="overview-stores-filter-search"
              />
              <div className="flex justify-between mt-2 text-xs">
                <button
                  type="button"
                  className="text-slate-600 hover:text-slate-900 underline"
                  data-testid="overview-stores-filter-select-all"
                  onClick={() => setSelectedStoreIds(visibleStores.map(s => s.store_id))}
                >Seleziona tutti</button>
                <button
                  type="button"
                  className="text-slate-600 hover:text-slate-900 underline"
                  data-testid="overview-stores-filter-deselect-all"
                  onClick={() => setSelectedStoreIds([])}
                >Deseleziona tutti</button>
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto py-1">
              {visibleStores.length === 0 && (
                <div className="px-3 py-4 text-xs text-slate-500">Nessun negozio per i brand selezionati.</div>
              )}
              {[...visibleStores]
                .sort((a, b) => (a.display_name || '').localeCompare(b.display_name || '', 'it', { sensitivity: 'base' }))
                .filter(s => !storeFilterQuery || s.display_name.toLowerCase().includes(storeFilterQuery.toLowerCase()))
                .map(s => {
                  const checked = selectedStoreIds.includes(s.store_id);
                  return (
                    <label
                      key={s.store_id}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer"
                      data-testid={`overview-stores-filter-item-${s.store_id}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          if (v) setSelectedStoreIds(prev => [...prev, s.store_id]);
                          else setSelectedStoreIds(prev => prev.filter(id => id !== s.store_id));
                        }}
                      />
                      <span className="truncate">{s.display_name}</span>
                    </label>
                  );
                })}
            </div>
          </PopoverContent>
        </Popover>
        {(selectedStoreIds.length > 0 || selectedBrandIds.length > 0 || selectedAreaIds.length > 0) && (
          <span className="text-xs text-slate-500">
            Vista limitata · ricavi e KPI calcolati solo sui filtri attivi
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <CopyLinkButton testId="overview-copy-link" />
          <SavedViewsControl
            scopeKey="overview"
            currentPayload={{ date_from: dateFrom, date_to: dateTo, store_ids: selectedStoreIds, brand_ids: selectedBrandIds, area_ids: selectedAreaIds }}
            onApply={(p) => {
              if (p?.date_from) setDateFrom(p.date_from);
              if (p?.date_to) setDateTo(p.date_to);
              setSelectedStoreIds(Array.isArray(p?.store_ids) ? p.store_ids : []);
              setSelectedBrandIds(Array.isArray(p?.brand_ids) ? p.brand_ids : []);
              setSelectedAreaIds(Array.isArray(p?.area_ids) ? p.area_ids : []);
            }}
            testIdPrefix="overview-saved-views"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Hero KPI strip — total + variance pills */}
          <HeroStrip data={data} periodLabel={periodLabel} />

          {/* Health score azienda + Briefing AI affiancati
              (refactor layout 2026-05-18, richiesta utente). */}
          {(data?.overall_health_score || isOps) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="overview-health-and-ai-row">
              {data?.overall_health_score ? (
                <OverviewHealthCard
                  health={data.overall_health_score}
                  periodLabel={periodLabel}
                />
              ) : <div />}
              {isOps ? (
                <ExecutiveSummary
                  month={repMonth} year={repYear} quarter={null} isAnnual={false}
                  periodLabel={periodLabel}
                  key={`${dateFrom}-${dateTo}-${refreshKey}`}
                />
              ) : <div />}
            </div>
          )}

          {/* Multi-brand cards */}
          <BrandsRow brands={data?.brands_summary || []} periodLabel={periodLabel} hiddenBrandIds={selectedBrandIds} />

          {/* Trend YTD + Top/Bottom side-by-side */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <TrendChart trend={data?.monthly_trend || []} year={year} />
            </div>
            <ActionsCard pendingActions={data?.pending_actions || 0} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <PerformersTable title="Top 5 — Migliori vs Budget" rows={data?.top5 || []} kind="top" period={String(repMonth)} year={repYear} />
            <PerformersTable title="Bottom 5 — Da seguire" rows={data?.bottom5 || []} kind="bottom" period={String(repMonth)} year={repYear} />
          </div>

          {/* Redditività globale + Top voci di costo */}
          {data?.profitability && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ProfitabilitySection
                profitability={data.profitability}
                periodLabel={periodLabel}
                title="Redditività globale"
                testId="overview-profitability-section"
              />
              <TopCostsChart
                profitability={data.profitability}
                title="Top voci di costo"
                testId="overview-top-costs-chart"
              />
            </div>
          )}

          {/* "Importazioni recenti" rimosso 2026-05-08 (idea_e245f72a3ede):
              duplicava info presenti in Stato Dati / Diagnostica. */}
        </div>
      )}
    </div>
  );
}

function HeroStrip({ data, periodLabel }) {
  return (
    <div className="bg-slate-900 text-white p-4 md:p-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">Ricavo totale</p>
          <p className="font-heading text-xl md:text-4xl font-bold mt-1 break-words">{fmtEUR(data?.total_revenue)}</p>
          <p className="text-xs text-slate-400 mt-1">{periodLabel}</p>
          <p className="text-[10px] text-slate-500 mt-0.5" data-testid="giftcard-note">Le giftcard sono escluse dal conteggio</p>
        </div>
        <DeltaTile label="vs Budget" value={data?.var_budget} pct={data?.var_budget_pct} base={data?.total_budget} />
        <DeltaTile label="vs Anno Prec." value={data?.var_ly} pct={data?.var_ly_pct} base={data?.total_ly} />
        <OccupancyTile
          actual={data?.occupancy_pct}
          budget={data?.occupancy_budget_pct}
          lastYear={data?.occupancy_ly_pct}
          storeCount={data?.store_count}
        />
        <ReviewsTile
          total={data?.total_reviews}
          lastYear={data?.total_reviews_ly}
          budget={data?.total_reviews_budget}
          varPct={data?.var_reviews_pct}
          varBudgetPct={data?.var_reviews_budget_pct}
        />
      </div>
    </div>
  );
}

function ReviewsTile({ total, lastYear, budget, varPct, varBudgetPct }) {
  // Recensioni Google guadagnate nel periodo (semantica DELTA daily, somma
  // mensile). Mostra il totale + variance vs Budget e vs A.P. con semaforo
  // verde se cresciamo / siamo sopra target.
  if (total == null) {
    return (
      <div data-testid="hero-reviews">
        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">Recensioni Google</p>
        <p className="font-heading text-xl md:text-4xl font-bold text-slate-500 mt-1">—</p>
      </div>
    );
  }
  const semLy = varPct == null ? 'neutral' : (varPct >= 0 ? 'green' : 'red');
  const semB = varBudgetPct == null ? 'neutral' : (varBudgetPct >= 0 ? 'green' : 'red');
  return (
    <div data-testid="hero-reviews" className="min-w-0">
      <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">Recensioni Google</p>
      <p className="font-heading text-xl md:text-4xl font-bold mt-1 break-words">{total.toLocaleString('it-IT')}</p>
      <p className="text-xs text-slate-400 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
        {varBudgetPct != null && (
          <span className={SEMAPHORE_TEXT[semB]}>
            vs Budget {varBudgetPct > 0 ? '+' : ''}{varBudgetPct.toFixed(1)}%
          </span>
        )}
        {varPct != null && (
          <span className={SEMAPHORE_TEXT[semLy]}>
            vs A.P. {varPct > 0 ? '+' : ''}{varPct.toFixed(1)}%
          </span>
        )}
        {varPct == null && varBudgetPct == null && (
          <span>nuove nel periodo</span>
        )}
        {budget != null && budget > 0 && (
          <span>· budget {budget.toLocaleString('it-IT')}</span>
        )}
      </p>
    </div>
  );
}

function OccupancyTile({ actual, budget, lastYear, storeCount }) {
  const fmtP = (v) => v == null ? '—' : `${v.toFixed(1)}%`;
  const dB = (actual != null && budget != null) ? actual - budget : null;
  const dLy = (actual != null && lastYear != null) ? actual - lastYear : null;
  const semB = semaphoreFromPp(dB);
  const semLy = semaphoreFromPp(dLy);
  return (
    <div data-testid="hero-occupancy" className="min-w-0">
      <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">Tasso di occupazione</p>
      <p className="font-heading text-xl md:text-4xl font-bold mt-1 break-words">{fmtP(actual)}</p>
      <p className="text-xs text-slate-400 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
        {dB != null && (
          <span className={SEMAPHORE_TEXT[semB]}>
            vs Budget {dB > 0 ? '+' : ''}{dB.toFixed(1)}pp
          </span>
        )}
        {dLy != null && (
          <span className={SEMAPHORE_TEXT[semLy]}>
            vs A.P. {dLy > 0 ? '+' : ''}{dLy.toFixed(1)}pp
          </span>
        )}
        {dB == null && dLy == null && <span>{storeCount || 0} negozi</span>}
      </p>
    </div>
  );
}

function DeltaTile({ label, value, pct, base }) {
  if (value == null) {
    return (
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">{label}</p>
        <p className="font-heading text-xl md:text-4xl font-bold text-slate-500 mt-1">—</p>
      </div>
    );
  }
  // Semaforo con tolleranza ±3% — coerente con i tooltip.
  const sem = semaphoreFromPct(pct) || (value >= 0 ? 'green' : 'red');
  const tone = SEMAPHORE_TEXT[sem];
  const Icon = value >= 0 ? TrendingUp : TrendingDown;
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">{label}</p>
      <p className={`font-heading text-xl md:text-4xl font-bold mt-1 flex items-center gap-1.5 md:gap-2 break-words ${tone}`}>
        <Icon className="w-5 h-5 md:w-6 md:h-6 opacity-70 shrink-0" />
        <span className="min-w-0 break-words">{value > 0 ? '+' : ''}{fmtEUR(value)}</span>
      </p>
      <p className="text-xs text-slate-400 mt-1 break-words">
        {pct != null && <span className={tone}>{fmtPct(pct)}</span>}
        {base != null && <> · base {fmtEUR(base)}</>}
      </p>
    </div>
  );
}

function ExecutiveSummary({ month, year, quarter, isAnnual, periodLabel }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [generated, setGenerated] = useState(false);
  const [open, setOpen] = useState(false);

  const params = { year };
  if (isAnnual) { /* */ }
  else if (quarter) params.quarter = quarter;
  else params.month = month;

  // Try to load cached summary on mount/period change
  useEffect(() => {
    let active = true;
    setSummary(null); setErr(''); setGenerated(false);
    aiApi.executiveSummary({ ...params, cache_only: true })
      .then(r => {
        if (!active) return;
        if (r.data?.summary_text) {
          setSummary(r.data);
          setGenerated(true);
        }
      })
      .catch(() => {});
    return () => { active = false; };
  // eslint-disable-next-line
  }, [month, year, quarter, isAnnual]);

  const generate = useCallback(async (force = false) => {
    setLoading(true); setErr('');
    try {
      const r = await aiApi.executiveSummary({ ...params, force });
      setSummary(r.data);
      setGenerated(true);
      // Apre automaticamente il popup dopo la generazione (richiesta utente
      // 2026-05-18). Resta richiudibile e ri-apribile col bottone "Apri".
      if (r.data?.summary_text) setOpen(true);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Errore AI');
    } finally { setLoading(false); }
  // eslint-disable-next-line
  }, [month, year, quarter, isAnnual]);

  const recap = summary?.summary_text
    ? summary.summary_text.length > 120
      ? summary.summary_text.slice(0, 118).trimEnd() + '…'
      : summary.summary_text
    : null;

  const generatedAtLabel = summary?.generated_at
    ? new Date(summary.generated_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
    : null;

  return (
    <>
      <div
        className="bg-white border border-slate-200 px-4 md:px-5 py-4 flex flex-col gap-3 min-h-full"
        data-testid="executive-summary"
      >
        <div className="min-w-0 flex-1">
          <h3 className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-violet-600" /> Briefing AI
          </h3>
          {recap && (
            <p
              className="text-sm font-heading font-semibold mt-1.5 leading-snug text-slate-800 line-clamp-2"
              data-testid="exec-summary-recap"
            >
              {recap}
            </p>
          )}
          {!recap && !err && (
            <p className="text-sm font-heading font-semibold text-slate-800 mt-1.5 leading-tight">
              Genera una sintesi AI di 2-3 frasi sul periodo.
            </p>
          )}
          {err && (
            <p className="text-sm text-red-600 mt-1.5">Errore: {err}</p>
          )}
          {generatedAtLabel && (
            <p
              className="text-[11px] text-slate-400 mt-2 truncate"
              title={`${generatedAtLabel} · ${summary.model}`}
            >
              <Clock className="w-3 h-3 inline-block mr-1 -mt-0.5" />
              {generatedAtLabel}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap mt-auto">
          {generated && (
            <Button
              onClick={() => setOpen(true)}
              size="sm"
              className="rounded-none h-8 text-xs bg-black text-white hover:bg-slate-900 flex-1 min-w-[110px]"
              data-testid="exec-summary-open"
            >
              <ExternalLink className="w-3 h-3 mr-1.5" />Apri briefing
            </Button>
          )}
          <Button
            onClick={() => generate(generated)}
            disabled={loading}
            variant={generated ? 'outline' : 'default'}
            size="sm"
            className={`rounded-none h-8 text-xs ${generated ? 'px-2.5' : 'bg-black text-white hover:bg-slate-900 flex-1 min-w-[120px]'}`}
            data-testid={generated ? 'exec-summary-regen' : 'exec-summary-generate'}
            title={generated ? 'Rigenera briefing' : 'Genera briefing AI'}
            aria-label={generated ? 'Rigenera' : 'Genera briefing'}
          >
            {generated
              ? <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              : <>
                  <Sparkles className={`w-3 h-3 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                  {loading ? 'Genero...' : 'Genera briefing'}
                </>}
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-2xl rounded-none p-0"
          data-testid="exec-summary-dialog"
        >
          <div className="px-6 py-5 border-b border-slate-200">
            <DialogHeader>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-violet-600" />
                <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">
                  Briefing direzionale
                </span>
              </div>
              <DialogTitle className="font-heading text-2xl font-bold tracking-tight">
                {periodLabel}
              </DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-6 py-6">
            {summary?.summary_text ? (
              <p
                className="text-base text-slate-800 leading-relaxed whitespace-pre-wrap"
                data-testid="exec-summary-text"
              >
                {summary.summary_text}
              </p>
            ) : (
              <p className="text-sm text-slate-400 italic">Nessun contenuto disponibile.</p>
            )}
            {summary?.generated_at && (
              <p className="text-[11px] text-slate-400 mt-6 uppercase tracking-wider flex items-center gap-1.5">
                <Clock className="w-3 h-3" />
                Generato il {summary.generated_at?.slice(0, 16).replace('T', ' ')} · {summary.model}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function BrandsRow({ brands, periodLabel, hiddenBrandIds = [] }) {
  // Quando l'utente filtra a uno specifico brand, gli altri brand non sono
  // "vuoti" — sono solo *nascosti* dal filtro. Li ometto per evitare il
  // copy "Ancora nessun negozio configurato" che sarebbe fuorviante.
  const visible = hiddenBrandIds.length > 0
    ? brands.filter(b => hiddenBrandIds.includes(b.brand_id))
    : brands;
  if (!visible.length) return null;
  // Tailwind needs static class names — pre-define per count
  const colsCls = visible.length === 1 ? 'md:grid-cols-1'
    : visible.length === 2 ? 'md:grid-cols-2'
    : visible.length === 3 ? 'md:grid-cols-3'
    : 'md:grid-cols-2 lg:grid-cols-4';
  return (
    <div>
      <h2 className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">
        Performance per brand · {periodLabel}
      </h2>
      <div className={`grid grid-cols-1 ${colsCls} gap-4`}>
        {visible.map(b => <BrandCard key={b.brand_id} brand={b} />)}
      </div>
    </div>
  );
}

function BrandCard({ brand }) {
  const isEmpty = brand.store_count === 0;
  const positive = brand.var_budget_pct != null && brand.var_budget_pct >= 0;

  if (isEmpty) {
    return (
      <Link
        to="/stores"
        className="bg-white border border-dashed border-slate-300 p-5 hover:border-slate-500 transition-colors group"
        data-testid={`brand-card-${brand.brand_id}`}
      >
        <p className="font-heading text-base font-semibold text-slate-400 group-hover:text-slate-700">{brand.name}</p>
        <p className="text-xs text-slate-400 mt-2">Ancora nessun negozio configurato</p>
        <p className="text-xs text-slate-700 mt-3 underline-offset-2 group-hover:underline">Aggiungi il primo →</p>
      </Link>
    );
  }

  return (
    <Link
      to={`/area-dashboard?brand=${brand.brand_id}`}
      className="bg-white border border-slate-200 p-5 hover:border-slate-900 transition-colors group block"
      data-testid={`brand-card-${brand.brand_id}`}
    >
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="min-w-0">
          <p className="font-heading text-base font-semibold text-slate-900 truncate">{brand.name}</p>
          <span className="text-[10px] text-slate-400 uppercase tracking-wider">{brand.store_count} {brand.store_count === 1 ? 'negozio' : 'negozi'}</span>
        </div>
        {brand.health_score && (
          <HealthBadge health={brand.health_score} size="sm" />
        )}
      </div>
      <p className="font-heading text-2xl font-bold text-slate-900">{fmtEUR(brand.revenue)}</p>
      <div className="mt-3 flex flex-col gap-1 text-xs">
        <div className="flex justify-between">
          <span className="text-slate-500">vs Budget</span>
          <span className={`font-medium ${brand.var_budget_pct == null ? 'text-slate-400' : positive ? 'text-emerald-700' : 'text-red-700'}`}>
            {fmtPct(brand.var_budget_pct)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">vs A.P.</span>
          <span className={`font-medium ${brand.var_ly_pct == null ? 'text-slate-400' : brand.var_ly_pct >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
            {fmtPct(brand.var_ly_pct)}
          </span>
        </div>
      </div>
      <p className="text-[10px] text-slate-400 mt-3 group-hover:text-slate-700">Apri Dashboard Area →</p>
    </Link>
  );
}

function TrendChart({ trend, year }) {
  const data = (trend || []).map(r => ({
    name: MONTH_SHORT[r.month - 1],
    Attuale: r.actual,
    Budget: r.budget,
    'Anno Prec.': r.last_year,
  }));
  return (
    <div className="bg-white border border-slate-200" data-testid="trend-chart">
      <div className="px-5 py-4 border-b border-slate-200">
        <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-700">
          Andamento mensile {year}
        </h2>
        <p className="text-xs text-slate-400 mt-1">Ricavi consolidati a livello azienda</p>
      </div>
      <div className="p-3" style={{ height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} stroke="#cbd5e1" />
            <YAxis
              tick={{ fontSize: 11, fill: '#64748b' }}
              stroke="#cbd5e1"
              tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
            />
            <ChartTooltip
              content={<SeriesTooltipContent fmt={fmtEUR} />}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="Attuale" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="Budget" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="5 5" dot={false} />
            <Line type="monotone" dataKey="Anno Prec." stroke="#1e3a8a" strokeWidth={2} strokeDasharray="2 4" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ActionsCard({ pendingActions }) {
  return (
    <Link
      to="/area-dashboard"
      className="bg-white border border-slate-200 p-5 hover:border-slate-900 transition-colors group flex flex-col justify-between"
      data-testid="actions-card"
    >
      <div>
        <div className="flex items-center gap-2 mb-3">
          <ClipboardList className="w-4 h-4 text-slate-400" />
          <h2 className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Piani d'azione AM</h2>
        </div>
        <p className="font-heading text-3xl md:text-5xl font-bold text-slate-900">{pendingActions}</p>
        <p className="text-xs text-slate-500 mt-2">
          {pendingActions === 0
            ? 'Nessun piano d\'azione registrato dagli AM al momento.'
            : pendingActions === 1
              ? 'Piano d\'azione AM legato a una criticità AI'
              : `Piani d'azione AM in carico per le criticità segnalate dall'AI`}
        </p>
      </div>
      <p className="text-[10px] text-slate-400 mt-4 group-hover:text-slate-700">
        Vai alla Dashboard Area per il dettaglio →
      </p>
    </Link>
  );
}

function PerformersTable({ title, rows, kind, period, year }) {
  const navigate = useNavigate();
  if (!rows.length) {
    return (
      <div className="bg-white border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-700">{title}</h2>
        </div>
        <p className="p-8 text-center text-sm text-slate-400">Nessun dato sufficiente</p>
      </div>
    );
  }
  const Icon = kind === 'top' ? ChevronUp : ChevronDown;
  // Drill-down: ogni riga porta direttamente alla store dashboard pre-filtrata
  // sullo stesso periodo dell'overview, con `kpi=total_sales` evidenziato (è il
  // KPI confrontato in queste tabelle). Riduce i 3 click "Apri store → cambia
  // periodo → trova KPI in tabella" a 1 click.
  const buildHref = (storeId) => {
    const params = new URLSearchParams();
    if (period != null) params.set('period', String(period));
    if (year != null) params.set('year', String(year));
    params.set('kpi', 'total_sales');
    return `/stores/${storeId}?${params.toString()}`;
  };
  return (
    <div className="bg-white border border-slate-200" data-testid={`performers-${kind}`}>
      <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
        <Icon className={`w-4 h-4 ${kind === 'top' ? 'text-emerald-600' : 'text-red-600'}`} />
        <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-700">{title}</h2>
      </div>
      <table className="w-full data-table">
        <thead>
          <tr><th>Negozio</th><th>Area</th><th className="text-right">Ricavi</th><th className="text-right">vs Budget</th></tr>
        </thead>
        <tbody>
          {rows.map((s, i) => (
            <tr
              key={s.store_id}
              data-testid={`perf-row-${kind}-${i}`}
              className="cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={(e) => {
                // Lascia stare se l'utente ha cliccato sul link interno (gestisce
                // navigazione + cmd+click per nuova tab nativamente).
                if (e.target.closest('a')) return;
                navigate(buildHref(s.store_id));
              }}
            >
              <td>
                <div className="flex items-center gap-2.5">
                  {s.health_score && <HealthBadge health={s.health_score} size="sm" />}
                  <Link
                    to={buildHref(s.store_id)}
                    className="font-medium text-slate-900 hover:underline truncate"
                    data-testid={`perf-row-${kind}-${i}-link`}
                  >
                    {s.display_name}
                  </Link>
                </div>
              </td>
              <td className="text-sm text-slate-500">{s.area || '—'}</td>
              <td className="num text-sm">{fmtEUR(s.revenue)}</td>
              <td className="num">
                <span className={`font-semibold ${s.var_pct >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {fmtPct(s.var_pct)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Filtro brand multi-select. Mantengo lo stesso shape (Popover + Checkbox)
// del filtro stores per coerenza visiva. Mostro un trigger compatto perché
// di solito i brand sono <5 — niente ricerca, solo lista flat.
function BrandFilter({ brands, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const total = brands.length;
  const triggerLabel = selected.length === 0
    ? `Tutti i brand (${total})`
    : selected.length === 1
      ? brands.find(b => b.brand_id === selected[0])?.name || '1 brand'
      : `${selected.length} brand selezionati`;
  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter(x => x !== id));
    else onChange([...selected, id]);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="rounded-none h-9 px-3 justify-between font-normal min-w-[12rem]"
          data-testid="overview-brands-filter-trigger"
        >
          <span className="flex items-center gap-2 text-sm truncate">
            <Building2 className="w-4 h-4 text-slate-500" />
            {triggerLabel}
          </span>
          {selected.length > 0 && (
            <X
              className="w-3.5 h-3.5 text-slate-500 hover:text-slate-900 ml-2"
              data-testid="overview-brands-filter-clear"
              onClick={(e) => { e.stopPropagation(); onChange([]); }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-0 rounded-none" align="start">
        <div className="py-1">
          {brands.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-500">Nessun brand.</div>
          )}
          {brands.map(b => {
            const checked = selected.includes(b.brand_id);
            return (
              <label
                key={b.brand_id}
                className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer"
                data-testid={`overview-brands-filter-item-${b.brand_id}`}
              >
                <Checkbox checked={checked} onCheckedChange={() => toggle(b.brand_id)} />
                <span className="truncate">{b.name}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}



/**
 * Filtro Aree multi-select — coerente con BrandFilter (pattern Popover +
 * checkbox lista). Mostra il count di store per area come scorciatoia.
 */
function PresetBtn({ children, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left text-xs px-2 py-1.5 hover:bg-slate-50 border border-transparent hover:border-slate-200 rounded-none"
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function AreaFilter({ areas, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const total = areas.length;
  if (total === 0) return null;
  const triggerLabel = selected.length === 0
    ? `Tutte le aree (${total})`
    : selected.length === 1
      ? selected[0]
      : `${selected.length} aree selezionate`;
  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter(x => x !== id));
    else onChange([...selected, id]);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="rounded-none h-9 px-3 justify-between font-normal min-w-[12rem]"
          data-testid="overview-areas-filter-trigger"
        >
          <span className="flex items-center gap-2 text-sm truncate">
            <MapPin className="w-4 h-4 text-slate-500" />
            {triggerLabel}
          </span>
          {selected.length > 0 && (
            <X
              className="w-3.5 h-3.5 text-slate-500 hover:text-slate-900 ml-2"
              data-testid="overview-areas-filter-clear"
              onClick={(e) => { e.stopPropagation(); onChange([]); }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1 rounded-none" align="start">
        <div className="max-h-64 overflow-y-auto py-1">
          {areas.map(a => {
            const checked = selected.includes(a.area);
            return (
              <label
                key={a.area}
                className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-50 cursor-pointer"
                data-testid={`overview-areas-filter-item-${a.area}`}
              >
                <Checkbox checked={checked} onCheckedChange={() => toggle(a.area)} />
                <span className="truncate flex-1">{a.area}</span>
                <span className="text-[11px] text-slate-400">{a.store_count}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Health Score della Panoramica con identità "Briefing direzionale".
 * Banda colorata a sinistra coerente con la band (verde/giallo/rosso),
 * stessa cifra stilistica del briefing AI sotto. (idea_e245f72a3ede)
 */
function OverviewHealthCard({ health, periodLabel }) {
  const band = health.band;
  const borderCls = band === 'green' ? 'border-l-emerald-500'
    : band === 'yellow' ? 'border-l-amber-500'
    : band === 'red' ? 'border-l-red-500'
    : 'border-l-slate-300';
  const iconCls = band === 'green' ? 'text-emerald-600'
    : band === 'yellow' ? 'text-amber-600'
    : band === 'red' ? 'text-red-600'
    : 'text-slate-400';
  const headline = health.score == null
    ? 'Non abbastanza dati di budget per questo periodo'
    : band === 'green' ? 'Andamento buono'
    : band === 'yellow' ? 'In linea con attenzione'
    : 'Sotto target — intervento necessario';
  return (
    <div
      className={`bg-white border-l-4 ${borderCls} border-y border-r border-slate-200 p-5 flex items-center gap-5`}
      data-testid="overview-health-score"
    >
      <HealthBadge health={health} size="lg" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <TrendingUp className={`w-4 h-4 ${iconCls}`} />
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-700">
            Health score azienda · {periodLabel}
          </h2>
        </div>
        <p className="font-heading text-base md:text-lg font-semibold mt-2 leading-tight text-slate-800">
          {headline}
        </p>
        {health.score != null && (
          <p className="text-xs text-slate-500 mt-1.5">
            {health.stores_with_data}/{health.stores_total} negozi con dati di budget
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * FreshaUnmatchedBanner — spostato in components/FreshaUnmatchedBanner.js
 * (condiviso con StaffPage, 2026-08-01).
 */

